<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import moment from "moment";
import OccupancyChart from "@/components/charts/OccupancyChart.vue";
import LockerMap from "@/components/charts/LockerMap.vue";

const store = useStore();

const selectedOrganization = ref("all");
const selectedPeriod = ref(30);
const isLoading = ref(true);
const PERIODS = [7, 30, 90, 180];

const lineColor = '#0040DD';
const chartData = ref({
  labels: [],
  datasets: [
    {
      label: 'Occupancy Rate',
      data: [],
      borderColor: lineColor,
      borderWidth: 2.5,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: lineColor,
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      tension: 0.35,
      fill: true,
      backgroundColor: (context) => {
        const { ctx, chartArea } = context.chart;
        if (!chartArea) return 'rgba(0,64,221,0.12)';
        const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, 'rgba(0,64,221,0.20)');
        g.addColorStop(1, 'rgba(0,64,221,0)');
        return g;
      }
    }
  ]
});

const occupancyStatistic = computed(() => store.getters['statistic/occupancyStatistic']);
const occupancyStatisticError = computed(() => store.getters['statistic/occupancyStatisticError']);
const dashboardStatistic = computed(() => store.getters['statistic/dashboardStatistic']);
const dashboardStatisticError = computed(() => store.getters['statistic/dashboardStatisticError']);
const organizationList = computed(() => store.getters['organization/organizationList']);

const dashboardData = computed(() => {
  if (!dashboardStatistic.value) return null;
  const data = dashboardStatistic.value;
  if (data && typeof data === 'object') {
    return data.data || data.result || data;
  }
  return data;
});

const hasError = computed(() => occupancyStatisticError.value || dashboardStatisticError.value);
const isDataLoading = computed(() => isLoading.value || (!dashboardData.value && !hasError.value));

const hasChartData = computed(() => {
  return occupancyStatistic.value &&
      occupancyStatistic.value.labels &&
      occupancyStatistic.value.datasets &&
      occupancyStatistic.value.labels.length > 0 &&
      occupancyStatistic.value.datasets.length > 0;
});

const recentOrders = computed(() => {
  if (!dashboardData.value?.recent_orders || !Array.isArray(dashboardData.value.recent_orders)) return [];
  return dashboardData.value.recent_orders;
});

const recentLogs = computed(() => {
  if (!dashboardData.value?.recent_logs || !Array.isArray(dashboardData.value.recent_logs)) return [];
  return dashboardData.value.recent_logs.slice(0, 6);
});

const organizations = computed(() => {
  const orgs = [{ id: "all", name: "All organizations" }];
  if (organizationList.value?.results && Array.isArray(organizationList.value.results)) {
    organizationList.value.results.forEach(org => {
      if (org && org.id && org.name) orgs.push({ id: org.id, name: org.name });
    });
  }
  return orgs;
});

// ---- KPI tiles ----
function deltaOf(value) {
  const n = Number(value);
  if (!n || Number.isNaN(n)) return null;
  return { dir: n > 0 ? 'up' : 'down', label: (n > 0 ? '+' : '−') + Math.abs(Math.round(n * 10) / 10) };
}

const kpis = computed(() => {
  const d = dashboardData.value || {};
  const online = d.online || 0;
  const offline = d.offline || 0;
  return [
    {
      icon: 'access-point', label: 'Online Lockers', value: online,
      delta: deltaOf(d.online_from_yesterday), foot: `of ${online + offline} total devices`,
    },
    {
      icon: 'access-point-off', label: 'Offline Lockers', value: offline,
      delta: deltaOf(d.offline_from_yesterday), foot: 'not reporting to the network',
    },
    {
      icon: 'view-grid-outline', label: 'Network Occupancy', value: d.occupancy || 0, unit: '%',
      delta: deltaOf(d.occupancy_from_yesterday), foot: 'daily average across lockers',
    },
    {
      icon: 'timer-sand', label: 'Avg Rental Time', value: d.rental_avg || 0, unit: 'h',
      delta: deltaOf(d.rental_avg_from_yesterday), foot: 'rolling 30-day average',
    },
  ];
});

// ---- chart data ----
const updateChartData = () => {
  if (occupancyStatistic.value?.labels && occupancyStatistic.value?.datasets) {
    chartData.value.labels = occupancyStatistic.value.labels.map(date => {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    let datasetValues;
    if (Array.isArray(occupancyStatistic.value.datasets)) {
      if (typeof occupancyStatistic.value.datasets[0] === 'number') {
        datasetValues = occupancyStatistic.value.datasets;
      } else {
        datasetValues = occupancyStatistic.value.datasets.map(dataset => dataset.data || dataset.value || 0);
      }
    } else {
      datasetValues = [];
    }
    chartData.value.datasets[0].data = datasetValues;
  }
};

watch(occupancyStatistic, (newValue) => {
  if (newValue) updateChartData();
}, { immediate: true });

const loadOccupancyData = async (days = selectedPeriod.value, organizationId = selectedOrganization.value) => {
  try {
    await store.dispatch('statistic/getOccupancyStatistic', { days, organization: organizationId });
    updateChartData();
  } catch (error) {
    console.error('Error loading occupancy data:', error);
  }
};

const loadDashboardData = async (organizationId = selectedOrganization.value) => {
  try {
    await store.dispatch('statistic/getDashboardStatistic', organizationId);
  } catch (error) {
    console.error('Error loading dashboard data:', error);
  }
};

const reloadAllData = async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      loadOccupancyData(selectedPeriod.value, selectedOrganization.value),
      loadDashboardData(selectedOrganization.value)
    ]);
  } finally {
    isLoading.value = false;
  }
};

const setPeriod = async (days) => {
  if (selectedPeriod.value === days) return;
  selectedPeriod.value = days;
  await loadOccupancyData(days, selectedOrganization.value);
};

watch(selectedOrganization, (newOrg, oldOrg) => {
  if (newOrg !== oldOrg) reloadAllData();
});

onMounted(async () => {
  try {
    isLoading.value = true;
    const promises = [
      loadOccupancyData(selectedPeriod.value, selectedOrganization.value),
      loadDashboardData(selectedOrganization.value)
    ];
    if (!store.getters['organization/organizationList']) {
      promises.push(store.dispatch('organization/getOrganizationList', '?page=1'));
    }
    await Promise.all(promises);
  } finally {
    isLoading.value = false;
  }
});

// ---- formatting helpers ----
const formatPrice = (amount) => {
  if (amount === undefined || amount === null) return '';
  return `$${Number(amount).toFixed(2)}`;
};

const timeAgo = (timestamp) => (timestamp ? moment(timestamp).fromNow() : '');

const formatLogMessage = (log) => {
  if (!log || !log.type) return 'Unknown log entry';
  switch (log.type) {
    case 'status_changed': {
      const device = log.device || 'Unknown device';
      const oldStatus = log.extra?.old_status || 'unknown';
      const newStatus = log.extra?.new_status || 'unknown';
      return `${device}: ${oldStatus} → ${newStatus}`;
    }
    default:
      return `${log.device || ''} ${log.type}`.trim();
  }
};

const logSeverity = (log) => {
  if (!log || !log.type) return 'info';
  switch (log.type) {
    case 'status_changed':
      return log.extra?.new_status === 'online' ? 'success' : 'warning';
    default:
      return 'info';
  }
};

const logIcon = (log) => {
  const sev = logSeverity(log);
  if (log?.type === 'status_changed') {
    return log.extra?.new_status === 'online' ? 'access-point' : 'access-point-off';
  }
  return sev === 'info' ? 'information-outline' : 'alert-circle-outline';
};
</script>

<template>
  <!-- organization filter lives in the topbar -->
  <Teleport to="#topbar-actions">
    <div class="field-select">
      <span class="mdi mdi-office-building-outline lead"></span>
      <select v-model="selectedOrganization">
        <option v-for="org in organizations" :key="org.id" :value="org.id">
          {{ org.name }}
        </option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
  </Teleport>

  <div v-if="hasError" class="card table-empty">
    Failed to load dashboard data. Please try refreshing the page.
  </div>

  <div class="kpi-tiles">
    <div v-for="k in kpis" :key="k.label" class="tile">
      <span class="tile__wm mdi" :class="`mdi-${k.icon}`"></span>
      <div class="tile__top">
        <div class="tile__label">{{ k.label }}</div>
        <span v-if="k.delta" class="tile__delta" :class="k.delta.dir">
          <span class="mdi" :class="k.delta.dir === 'up' ? 'mdi-trending-up' : 'mdi-trending-down'"></span>
          {{ k.delta.label }}
        </span>
      </div>
      <div class="tile__value">
        {{ isDataLoading ? '—' : k.value }}<small v-if="k.unit">{{ k.unit }}</small>
      </div>
      <div class="tile__foot">{{ k.foot }}</div>
    </div>
  </div>

  <div class="grid-2">
    <div class="card chart">
      <div class="card__head">
        <div>
          <h2>Network Occupancy</h2>
          <div class="sub">Daily average across online lockers</div>
        </div>
        <div class="spacer"></div>
        <div class="seg">
          <button
            v-for="p in PERIODS"
            :key="p"
            type="button"
            :class="{ 'is-active': selectedPeriod === p }"
            @click="setPeriod(p)">
            {{ p }}d
          </button>
        </div>
      </div>
      <div class="chart__body">
        <OccupancyChart v-if="hasChartData" :data="chartData" />
        <div v-else class="table-empty" style="width: 100%">
          {{ isDataLoading ? 'Loading chart data…' : 'No chart data available' }}
        </div>
      </div>
    </div>

    <div class="card orders">
      <div class="card__head">
        <h2>Recent Orders</h2>
        <div class="spacer"></div>
        <router-link to="/orders" class="card__link">
          View all <span class="mdi mdi-arrow-right"></span>
        </router-link>
      </div>
      <div v-if="recentOrders.length" class="orders__list">
        <div v-for="order in recentOrders" :key="order.id" class="order">
          <div class="order__ico">
            <span class="mdi mdi-package-variant-closed"></span>
          </div>
          <div class="order__main">
            <b>#{{ order.id }}</b>
            <span>{{ formatPrice(order.price) }}{{ order.finished_at ? ' · finished' : ' · active' }}</span>
          </div>
          <div class="order__time">{{ timeAgo(order.created_at) }}</div>
        </div>
      </div>
      <div v-else class="table-empty">
        {{ isDataLoading ? 'Loading orders…' : 'No recent orders' }}
      </div>
    </div>
  </div>

  <div class="grid-2">
    <div class="card map">
      <div class="card__head">
        <div>
          <h2>Locker Network Map</h2>
          <div class="sub">Live device status across the network</div>
        </div>
      </div>
      <div class="map__canvas">
        <LockerMap />
      </div>
    </div>

    <div class="card orders">
      <div class="card__head">
        <h2>System Logs</h2>
        <div class="sub">Latest network events</div>
        <div class="spacer"></div>
        <router-link to="/devices-logs" class="card__link">
          View all <span class="mdi mdi-arrow-right"></span>
        </router-link>
      </div>
      <div v-if="recentLogs.length" class="orders__list">
        <div v-for="(log, i) in recentLogs" :key="log.created_at || i" class="evt">
          <div class="evt__ico" :class="logSeverity(log)">
            <span class="mdi" :class="`mdi-${logIcon(log)}`"></span>
          </div>
          <div class="evt__main">
            <b>{{ formatLogMessage(log) }}</b>
            <span>{{ log.organization || '' }}</span>
          </div>
          <div class="evt__time">{{ timeAgo(log.created_at) }}</div>
        </div>
      </div>
      <div v-else class="table-empty">
        {{ isDataLoading ? 'Loading logs…' : 'No system logs' }}
      </div>
    </div>
  </div>
</template>
